/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import mbpUtil from '@api-util/mbp';
import { useLocation } from 'react-router';
import { bool, string } from 'prop-types';
import mbpLogger from '@module-logger/mbp';
import MultiBrandChatButton from '../MultiBrandChatController/MultiBrandChatButton';
import ConversationalUIChatBot from '../ConversationalUI/ConversationalUIChatBot';
import { getFeatureFlag } from '../../../state/ducks/App/ducks/Config/Config-Selectors';
import { determineChatlink } from '../MultiBrandChatController/Global_chat_helpers';
import { getCurrentBrandId, getFeatureFlags } from '../../pages/Account/utils/featureSelectors';
import { getPageType } from '../../../state/ducks/App/App-Selectors';
import checkWhiteList from '../ConversationalUI/helper/WhiteList';
import { getOperatingSystem, getBrowser } from '../../../state/ducks/App/ducks/Config/Config-Helpers';
import useSalesforcePersonalizedContentQuery from '../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getBrandName } from '../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import getTrustedOrigin from '../ConversationalUI/helper/TrustedOrigin';

const ChatWrapper = ({
    appShellLoaded, ssrDeviceType, brand, brandTheme,
}) => {
    // BB Chat
    const [isChatBtnClicked, setIsChatBtnClicked] = useState(false);
    const [popOpenChatDetail, setPopOpenChatDetail] = useState({});
    const [categoryList, setCategoryList] = useState([]);
    const [isUseCaseTriggered, setIsUseCaseTriggered] = useState('');
    const [localStorageItem, setLocalStorageItem] = useState('');
    const cartCount = useSelector((state) => state?.order?.cartCount);
    const [initialCartCount] = useState(cartCount);
    const ffIsGenesysChatEnabled = useSelector(getFeatureFlag('is-genesys-chat-enabled'));

    // Conv Ui Chat
    const [isFloraChatBtnClicked, setIsFloraChatBtnClicked] = useState(false);
    const ffIsConvUIWhitelistEnabled = useSelector(getFeatureFlag('flora-chat-whitelist'));
    const ffIsConvUiGlobalEnabled = useSelector(getFeatureFlag('is-conv-ui-enabled'));
    const ffIsChatABTestEnabled = useSelector(getFeatureFlag('is-chatbot-ab-test-enabled'));
    const pageType = useSelector(getPageType);
    const brandName = useSelector(getBrandName);
    const operatingSystem = getOperatingSystem().toLowerCase();
    const currentBrand = useSelector((state) => getCurrentBrandId(state));
    const customerData = useSelector((state) => state?.member);
    const featureFlags = useSelector((state) => getFeatureFlags(state));
    const location = useLocation();
    let conversationalUIEnabled = false;
    if (ssrDeviceType === 'desktop') {
        // if desktop, read from the global flag
        conversationalUIEnabled = ffIsConvUiGlobalEnabled;
    } else {
        // if NOT desktop, then read from the mobile flag (array shape)
        conversationalUIEnabled = (ffIsConvUiGlobalEnabled && featureFlags?.['is-flora-chat-for-current-mobile-device-enabled']?.length && featureFlags?.['is-flora-chat-for-current-mobile-device-enabled']?.includes(operatingSystem?.toLowerCase()));
    }
    const {
        data: chatBotComparisonData, error: err, loading, variables,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'chatbotABTest',
        interactionName: `${brandName} - Get Campaign - AI Chatbot Test`,
        featureFlag: ffIsChatABTestEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });

    const fireClickStream = useClickStreamCustomEventsTracking({
        salesforceResponse: chatBotComparisonData?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });
    const chatBotComparisonDataUserGroup = chatBotComparisonData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const handleLoadMoreButtonClick = () => {
        if (
            featureFlags['is-chat-proactive-sales-usecase-enabled']
            && !featureFlags['is-csp-chat-widget-enabled']
        ) {
            // * Collection page use case: have hit the load more button twice without adding to cart
            try {
                const categoryData = JSON.parse(localStorage?.getItem('persistPageData') || {});
                setCategoryList((prevState) => {
                    const updatedState = [
                        ...prevState,
                        categoryData?.collection?.categoryName || 'no name given'];
                    // * if we havent hit load button twice yet AND if something HAS NOT been added to cart and chat hasnt been popped
                    if (updatedState?.length === 2 && initialCartCount >= cartCount && !isUseCaseTriggered && !isChatBtnClicked) {
                        setPopOpenChatDetail({ categoryNames: updatedState });
                        setIsUseCaseTriggered('proactive_sales_help');
                        setIsChatBtnClicked(true);
                    }
                    return updatedState;
                });
            } catch (error) {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    errorMessage: error,
                    message: `WebChat: proactive sales pop chat failure - ERROR: ${error}`,
                });
            }
        }
    };
    const openChatWithDetail = (detail) => {
        setPopOpenChatDetail(detail);
        setIsChatBtnClicked(true);
    };
    const handleMobileEvent = (event) => {
        const detail = event?.detail || {};
        if (detail.didSetPersistData) {
            setLocalStorageItem(detail.didSetPersistData);
            return;
        }
        if (detail.origin) {
            openChatWithDetail(detail);
            return;
        }

        const href = determineChatlink(
            operatingSystem,
            currentBrand,
            featureFlags,
            customerData,
        );
        if (window && typeof window !== 'undefined' && href) {
            window.location.href = href;
        }
    };
    const trustedOrigin = getTrustedOrigin(mbpUtil?.getEnv('APP_CONVERSATIONAL_UI_HOST'));
    const handleConversationalUI = () => {
        const floraIframeSelector = `iframe[src="${trustedOrigin}/chat/flora"]`;

        const iframe = document?.querySelector(floraIframeSelector);
        if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage(
                { type: 'trigger-flora-chat' },
                `${trustedOrigin}/chat/flora`,
            );
        }
    };
    const handleDesktopEvent = (event) => {
        const detail = event?.detail || {};
        if (detail.loadMoreButtonClick) {
            handleLoadMoreButtonClick();
        } else if (detail.didSetPersistData) {
            setLocalStorageItem(detail.didSetPersistData);
        } else if (detail.origin) {
            openChatWithDetail(detail);
        } else {
            setIsChatBtnClicked(true);
        }
    };
    // single handler for both chat buttons
    const popOpenChatHandler = (event) => {
        if (conversationalUIEnabled) {
            handleConversationalUI();
            return;
        }
        if (ssrDeviceType === 'desktop') {
            handleDesktopEvent(event);
        } else {
            handleMobileEvent(event);
        }
    };
    useEffect(() => {
        if ((isChatBtnClicked || isFloraChatBtnClicked) && chatBotComparisonData?.contentSF) {
            fireClickStream();
        }
    }, [isChatBtnClicked, isFloraChatBtnClicked, chatBotComparisonData?.contentSF]);
    useEffect(() => {
        if (window && typeof window !== 'undefined') {
            window.addEventListener('popOpenChat', popOpenChatHandler);
        }
        return () => {
            if (window && typeof window !== 'undefined') {
                window.removeEventListener('popOpenChat', popOpenChatHandler);
            }
        };
    }, []);
    let conversationalWhitelist = false;
    if (ffIsChatABTestEnabled && (loading || variables?.skip)) {
        return null;
    }
    if (ffIsChatABTestEnabled && !loading && (err || variables?.eidError)) {
        mbpLogger.logError({
            function: 'Chat Test - interactionName = 1800flowers - Get Campaign - AI Chatbot Test',
            message: 'Error loading API data',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: err,
        });
    }
    if (appShellLoaded && typeof window !== 'undefined' && trustedOrigin && ffIsConvUIWhitelistEnabled) {
        conversationalWhitelist = checkWhiteList(ffIsConvUIWhitelistEnabled);
    }
    if (appShellLoaded && typeof window !== 'undefined' && (conversationalUIEnabled || conversationalWhitelist || chatBotComparisonDataUserGroup === 'Test') && trustedOrigin) {
        if (ffIsChatABTestEnabled && conversationalUIEnabled && chatBotComparisonDataUserGroup !== 'Test' && !conversationalWhitelist) {
            // continue
        } else {
            return <ConversationalUIChatBot deviceType={ssrDeviceType} brand={brand} browser={getBrowser()} operatingSystem={getOperatingSystem()?.toLowerCase()} brandTheme={brandTheme} pageType={pageType} location={location} setIsFloraChatBtnClicked={setIsFloraChatBtnClicked} featureFlags={featureFlags} />;
        }
    }
    if (appShellLoaded && ffIsGenesysChatEnabled && ssrDeviceType !== 'mobile' && ssrDeviceType !== 'tablet' && typeof window !== 'undefined') {
        return <MultiBrandChatButton ssrDeviceType={ssrDeviceType} isChatBtnClicked={isChatBtnClicked} localStorageItem={localStorageItem} setIsChatBtnClicked={setIsChatBtnClicked} popOpenChatDetail={popOpenChatDetail} setPopOpenChatDetail={setPopOpenChatDetail} categoryList={categoryList} setCategoryList={setCategoryList} isUseCaseTriggered={isUseCaseTriggered} setIsUseCaseTriggered={setIsUseCaseTriggered} />;
    }
    return null;
};

ChatWrapper.propTypes = {
    appShellLoaded: bool.isRequired,
    ssrDeviceType: string,
    brand: string.isRequired,
    brandTheme: string.isRequired,
};
ChatWrapper.defaultProps = {
    ssrDeviceType: '',
};
export default ChatWrapper;
